import React from 'react';
import { includes, size, find } from 'lodash';
import styled from 'styled-components';
import { Tile, TileStatusEnum } from './Tile';
import { TileDropItem, HandState, TileGroupDropItem } from '../types/';
import { DumpSection } from './DumpSection';
import { useDrop } from 'react-dnd';
import { dndTypes, handHeight } from '../helpers/constants';
import { ActiveDropTarget } from '../containers/ActiveDropTarget';
import { SelectedTilesContainer } from '../containers/SelectedTilesContainer';
import { SelectedBoardTiles } from '../containers/SelectedBoardTiles';
import { Button } from './Button';

type HandProps = {
    className?: string,
    hand: HandState,
    onPeel?: () => void,
    onDump?: (id: number) => void,
    onSubmit?: () => void,
    onDrop: (item: TileDropItem) => void,
    onClickTile: (e: React.MouseEvent<HTMLDivElement>, id: number) => any,
};

const HandContainer =
    styled.div <
    { isOver: boolean } >
    `
    height: ${handHeight}px;
    overflow: hidden;
    border: 1px solid black;
    padding: 20px 0;
    background-color: ${(props) => (props.isOver ? 'lightblue' : props.theme.colors.brown)};
    display: flex;
    flex-direction: column;
    justify-content: space-between;
`;

const Tiles = styled.div`
    display: flex;
    flex-wrap: wrap;
    overflow: auto;
    height: auto;
    padding: 0 0 0 50px;
`;

const TileWithSpacing =
    styled(Tile) <
    { dragType: any } >
    `
    margin-right: 10px;
    margin-bottom: 10px;
    background-color: ${(props) => (!!props.dragType ? props.theme.colors.lightYellow : 'grey')}
`;

const ButtonContainer = styled.div`
    padding: 0 10px;
    text-align: center;
`;

export const Hand: React.FC<HandProps> = ({
    className,
    hand,
    onPeel,
    onDump,
    onDrop,
    onClickTile,
    onSubmit,
}) => {
    // useContainer is kinda bad because we lost props
    const { setDropTarget } = ActiveDropTarget.useContainer();
    const { selectedTiles, setSelectedTiles } = SelectedTilesContainer.useContainer();
    const { setSelectedBoardTiles } = SelectedBoardTiles.useContainer();
    const [{ isOver }, drop] = useDrop({
        accept: [dndTypes.TILE_FROM_BOARD, dndTypes.TILE_FROM_HAND, dndTypes.TILE_GROUP],
        drop: (item: TileDropItem | TileGroupDropItem, monitor) => {
            if (monitor.isOver({ shallow: false })) {
                if (item.type === dndTypes.TILE_FROM_BOARD) {
                    onDrop(item);
                } else if (item.type === dndTypes.TILE_GROUP) {
                    setSelectedTiles([]);
                }
            }
        },
        hover: () => {
            setDropTarget(dndTypes.HAND);
        },
        collect: (monitor) => {
            const item = monitor.getItem() || {};
            const isInHand = !!find(hand, { id: item.id });
            const isOver = monitor.isOver();
            return {
                isOver: !isInHand && isOver,
                canDrop: monitor.canDrop(),
            };
        },
    });
    return (
        <HandContainer isOver={isOver} className={className} ref={drop}>
            {!size(hand) ? (
                onSubmit ? (
                    <ButtonContainer>
                        <Button fullWidth={true} onClick={onSubmit}>
                            Submit
                        </Button>
                    </ButtonContainer>
                ) : (
                    <ButtonContainer>
                        <Button fullWidth={true} onClick={onPeel}>
                            Peel
                        </Button>
                    </ButtonContainer>
                )
            ) : null}
            <Tiles>
                {hand.map(({ id }) => {
                    const isSelected = includes(selectedTiles, id);
                    return (
                        <TileWithSpacing
                            onBeginDrag={() => {
                                setSelectedBoardTiles([]);
                            }}
                            dragType={!isSelected ? dndTypes.TILE_FROM_HAND : undefined}
                            status={isSelected ? TileStatusEnum.DISABLED : undefined}
                            onClick={onClickTile}
                            key={id}
                            id={id}
                            variant="hand"
                        />
                    );
                })}
            </Tiles>
            {onDump && (
                <ButtonContainer>
                    <DumpSection hand={hand} onDrop={(item) => onDump(item.id)} />
                </ButtonContainer>
            )}
        </HandContainer>
    );
};

